import { setAuthTokenHeaders, setAuthTokenHeadersForPage } from "../../../api/api";
import { getMetriportPatientId as athenaGetMetriportPatientId } from "../../../api/ehr/athena";
import { getMetriportPatientId as canvasGetMetriportPatientId } from "../../../api/ehr/canvas";
import { getMetriportPatientId as eclinicalworksGetMetriportPatientId } from "../../../api/ehr/eclinicalworks";
import { getMetriportPatientId as elationGetMetriportPatientId } from "../../../api/ehr/elation";
import { getMetriportPatientId as healthieGetMetriportPatientId } from "../../../api/ehr/healthie";
import { getMetriportPatientId as practicefusionGetMetriportPatientId } from "../../../api/ehr/practicefusion";
import { getMetriportPatientId as salesforceGetMetriportPatientId } from "../../../api/ehr/salesforce";
import {
  athenaHealthAuthPath,
  authPathSchema,
  canvasAuthPath,
  eclinicalworksAuthPath,
  elationAuthPath,
  healthieAuthPath,
  isPageAuthPath,
  pageAuthPathSchema,
  practicefusionAuthPath,
  salesforceAuthPath,
} from "../../../domain/ehr";
import {
  AuthScopeDetails,
  AuthScopes,
  authScopeSchemaMap,
  PageAuthScope,
  PatientAuthScope,
  rawScopeShema,
  RawScopeType,
} from "./scope";

/** Type for the scope determination result */
type ScopeType = typeof AuthScopes.patient | typeof AuthScopes.page;

/** Handler function type - pure function signature */
type ScopeHandler = (details: AuthScopeDetails) => Promise<AuthScopeDetails>;

/** Lookup table for EHR patient ID fetchers */
const ehrPatientIdFetchers: Record<string, (ehrPatientId: string) => Promise<unknown>> = {
  [athenaHealthAuthPath]: athenaGetMetriportPatientId,
  [canvasAuthPath]: canvasGetMetriportPatientId,
  [elationAuthPath]: elationGetMetriportPatientId,
  [healthieAuthPath]: healthieGetMetriportPatientId,
  [eclinicalworksAuthPath]: eclinicalworksGetMetriportPatientId,
  [salesforceAuthPath]: salesforceGetMetriportPatientId,
  [practicefusionAuthPath]: practicefusionGetMetriportPatientId,
};

function determineScope(rawScope: RawScopeType): ScopeType {
  const isPatientScoped = !!rawScope.parsedHash.get("patient");
  const isPageScoped = isPageAuthPath(rawScope.path);

  if (isPatientScoped) return AuthScopes.patient;
  if (isPageScoped) return AuthScopes.page;
  throw new Error("Invalid Scope");
}

function extractAuthDetails(rawScope: RawScopeType, scopeType: ScopeType): AuthScopeDetails {
  const path = rawScope.path;
  const token = rawScope.parsedHash.get("access_token") ?? undefined;
  const ehrPatientId = rawScope.parsedHash.get("patient") ?? undefined;
  const ehrPractitionerId = rawScope.parsedHash.get("practitionerId") ?? undefined;

  return authScopeSchemaMap[scopeType].parse({
    ...(token ? { token } : {}),
    ...(ehrPatientId ? { ehrPatientId } : {}),
    ...(ehrPractitionerId ? { ehrPractitionerId } : {}),
    ...(path ? { path } : {}),
  });
}

async function handlePatientScope(details: PatientAuthScope): Promise<PatientAuthScope> {
  const { path, ehrPatientId, token, ehrPractitionerId } = details;
  const ehrAuthPath = authPathSchema.parse({ authPath: path });

  setAuthTokenHeaders(token, ehrAuthPath);

  const fetcher = ehrPatientIdFetchers[ehrAuthPath.authPath];
  if (fetcher) {
    await fetcher(ehrPatientId);
  }

  const includesPractitioner = ehrAuthPath.authPath === canvasAuthPath;
  return includesPractitioner
    ? { path, ehrPatientId, token, ehrPractitionerId }
    : { path, ehrPatientId, token };
}

async function handlePageScope(details: PageAuthScope): Promise<PageAuthScope> {
  const { path, token } = details;
  const parsed = pageAuthPathSchema.parse(path);
  setAuthTokenHeadersForPage(token, parsed);
  return details;
}

const scopeHandlers: Record<ScopeType, ScopeHandler> = {
  [AuthScopes.patient]: handlePatientScope as ScopeHandler,
  [AuthScopes.page]: handlePageScope as ScopeHandler,
};

async function initAuthScope(rawScope: RawScopeType): Promise<AuthScopeDetails> {
  const scopeData = rawScopeShema.parse(rawScope);
  const scopeType = determineScope(scopeData);
  const details = extractAuthDetails(scopeData, scopeType);
  const handler = scopeHandlers[scopeType];
  return handler(details);
}

export { initAuthScope, handlePatientScope, handlePageScope };
